<template>
  <div>
    <!-- Forsendelsestype -->
    <div class="mb-3 flex items-center gap-2">
      <span class="text-xs font-medium uppercase tracking-wider text-text-muted">
        {{ shipmentTypeLabel }}
      </span>
    </div>

    <!-- Pending / in-progress -->
    <div
      v-if="shipment.status === 'pending' || shipment.status === 'in-progress'"
      class="flex flex-col gap-2">
      <div class="flex items-center gap-2 text-sm text-text-muted">
        <IconLucideLoader2 class="animate-spin" />
        <span>{{ $t('area.dhl.shipment.message.creating') }}</span>
      </div>
      <div
        v-if="isStuck"
        class="flex flex-col gap-2">
        <span class="text-sm text-text-muted">
          {{ $t('area.dhl.shipment.message.taking-longer') }}
        </span>
        <UiBaseButton
          :text="$t('area.dhl.shipment.action.retry')"
          :isLoading="isRetrying"
          theme="neutral"
          size="sm"
          @click="onRetry" />
      </div>
    </div>

    <!-- Failed -->
    <div
      v-else-if="shipment.status === 'failed'"
      class="flex flex-col gap-2">
      <span class="text-sm font-medium text-red-600">
        {{ $t('area.dhl.shipment.state.failed-title') }}
      </span>
      <span
        v-if="failedStepError"
        class="text-sm text-text-muted">
        {{ failedStepError }}
      </span>

      <div
        v-if="!isPickingNewDate"
        class="flex gap-2">
        <UiBaseButton
          :text="$t('area.dhl.shipment.action.retry')"
          :isLoading="isRetrying"
          size="sm"
          @click="onRetry" />
        <UiBaseButton
          v-if="shipment.type === 'private-drop-point'"
          :text="$t('area.dhl.drop-point.action.choose-new-date')"
          theme="neutral"
          size="sm"
          @click="onPickNewDate" />
      </div>

      <!-- Cutoff on the stored date has passed — retrying with the same date
           would just fail again, so let the user pick a new one instead. -->
      <div
        v-else
        class="flex flex-col gap-2">
        <p
          v-if="isLoadingNewDates"
          class="text-sm text-text-muted">
          {{ $t('area.dhl.drop-point.message.loading-dates') }}
        </p>
        <p
          v-else-if="newAvailableDates.length === 0"
          class="text-sm text-text-muted">
          {{ $t('area.dhl.drop-point.message.no-pickup-10-days') }}
        </p>
        <div
          v-else
          class="flex flex-col gap-2">
          <div
            v-for="date in newAvailableDates"
            :key="date.date"
            class="cursor-pointer rounded-lg border p-3 transition-colors"
            :class="
              selectedNewDate === date.date
                ? 'border-action bg-surface-muted'
                : 'border-border-subtle hover:border-border-strong'
            "
            @click="selectedNewDate = date.date">
            <p class="text-sm font-semibold">{{ formatNewDate(date.date) }}</p>
            <p
              v-if="date.localCutoffDateAndTime"
              class="mt-0.5 text-xs text-text-muted">
              {{ formatNewCutoff(date.localCutoffDateAndTime) }}
            </p>
          </div>
        </div>
        <div class="flex gap-2">
          <UiBaseButton
            :text="$t('action.cancel')"
            theme="neutral"
            size="sm"
            @click="isPickingNewDate = false" />
          <UiBaseButton
            :text="$t('action.confirm')"
            :isLoading="isRetrying"
            :isDisabled="!selectedNewDate"
            size="sm"
            @click="onRetryWithNewDate" />
        </div>
      </div>
    </div>

    <!-- Completed -->
    <div
      v-else-if="shipment.status === 'completed' && shipment.result"
      class="flex flex-col gap-4">
      <!-- QR code -->
      <div v-if="shipment.result.labelFreeCodePath">
        <img
          v-if="qrCodeUrl"
          :src="qrCodeUrl"
          alt="DHL QR-kode"
          class="h-40 w-40" />
        <span
          v-else-if="isLoadingQrCode"
          class="text-sm text-text-muted">
          {{ $t('area.dhl.shipment.message.loading-qr') }}
        </span>
        <span
          v-else-if="qrCodeError"
          class="text-sm text-red-600">
          {{ qrCodeError }}
        </span>
        <span class="mt-1 block text-xs text-text-muted">
          {{ $t('area.dhl.shipment.message.scan-qr-at-carrier') }}
        </span>
      </div>

      <!-- Tracking code (not shown for service point / LabelFree orders) -->
      <div
        v-if="!shipment.result.labelFreeCodePath"
        class="flex flex-col gap-0.5">
        <span class="text-xs font-medium uppercase tracking-wider text-text-muted">
          {{ $t('area.dhl.shipment.field.tracking-code-narrow') }}
        </span>
        <span class="font-mono text-lg font-semibold text-text-primary">
          {{ shipment.result.trackingCode }}
        </span>
      </div>

      <!-- Shipping label (private drop point / pickup orders only) -->
      <div
        v-if="shipment.result.labelDocumentPath"
        class="flex flex-col gap-1">
        <UiBaseButton
          :text="$t('area.dhl.shipment.action.download-label')"
          theme="neutral"
          size="sm"
          :isLoading="isLoadingLabel"
          @click="onDownloadLabel" />
        <span
          v-if="labelError"
          class="text-sm text-red-600">
          {{ labelError }}
        </span>
      </div>

      <!-- Estimated pickup time (not shown for service point orders) -->
      <div
        v-if="!shipment.result.labelFreeCodePath && shipment.result.estimatedPickupTime"
        class="flex flex-col gap-0.5">
        <span class="text-xs font-medium uppercase tracking-wider text-text-muted">
          {{ $t('area.dhl.shipment.field.pickup-deadline') }}
        </span>
        <span class="text-sm text-text-primary">
          {{ formattedPickupTime }}
        </span>
      </div>

      <!-- Estimated lab arrival (not shown for service point orders) -->
      <div
        v-if="!shipment.result.labelFreeCodePath && shipment.result.estimatedPickupTime"
        class="flex flex-col gap-0.5">
        <span class="text-xs font-medium uppercase tracking-wider text-text-muted">
          {{ $t('area.dhl.shipment.field.estimated-lab-arrival') }}
        </span>
        <span class="text-sm text-text-primary">
          {{ formattedLabArrival }}
        </span>
        <span class="text-xs text-text-muted">
          {{ $t('area.dhl.order.message.tat-updates-on-arrival') }}
        </span>
      </div>

      <!-- DHL tracking status (fetched on demand) -->
      <div class="flex flex-col gap-2">
        <UiBaseButton
          :text="trackingResult ? $t('area.dhl.shipment.action.refresh-tracking') : $t('area.dhl.shipment.action.show-tracking')"
          theme="neutral"
          size="sm"
          class="self-start"
          :isLoading="isLoadingTracking"
          @click="onShowTracking" />

        <span
          v-if="trackingError"
          class="text-sm text-red-600">
          {{ trackingError }}
        </span>

        <div
          v-if="trackingResult"
          class="flex flex-col gap-2">
          <span class="text-xs font-medium uppercase tracking-wider text-text-muted">
            {{ $t('area.dhl.shipment.field.tracking-status') }}
          </span>
          <span class="text-sm font-semibold text-text-primary">
            {{ trackingResult.status ?? $t('area.dhl.status.unknown') }}
          </span>

          <div
            v-if="trackingResult.events.length"
            class="mt-1 flex flex-col gap-2">
            <div
              v-for="(event, index) in trackingResult.events"
              :key="index"
              class="flex flex-col gap-0.5 border-l-2 border-border-subtle pl-3">
              <span class="text-xs text-text-muted">{{ formatTrackingEventDate(event) }}</span>
              <span class="text-sm text-text-primary">{{ event.description || event.typeCode || '—' }}</span>
            </div>
          </div>
          <span
            v-else
            class="text-sm text-text-muted">
            {{ $t('area.dhl.shipment.message.no-tracking-events') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import type { IDhlShipment } from '@team-milva/lab-models/resources'
import { mapActions, mapState } from 'pinia'
import { useOrderStore } from '@/stores/orderStore'
import { useAccountStore } from '@/stores/accountStore'
import { useAppStore } from '@/stores/appStore'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useToast } from '@/globals/typescript/utils/toast'
import { storage } from '@/firebase/init'
import { getDownloadURL, ref as storageRef } from 'firebase/storage'

type TAvailableDeliveryDate = {
  date: string
  localCutoffDateAndTime: string | null
  estimatedDeliveryDateAndTime: string | null
  pickupEarliest: string | null
  pickupLatest: string | null
  price: number | null
  priceCurrency: string | null
}

type TDhlTrackingEvent = {
  date: string | null
  time: string | null
  typeCode: string | null
  description: string | null
}

type TDhlTrackingResult = {
  status: string | null
  events: TDhlTrackingEvent[]
}

/** A real DHL rates/shipment call completes in seconds, even accounting for a Cloud Functions
 *  cold start — 1 minute leaves a comfortable safety margin while still surfacing a retry
 *  option quickly, rather than waiting for the scheduled backend backstop
 *  (`checkStuckDhlShipments`, 30m) to fire. */
const STUCK_THRESHOLD_MS = 60 * 1000

export default {
  name: 'DhlShipmentCard',

  props: {
    shipment: { type: Object as PropType<IDhlShipment>, required: true },
    orderID: { type: String, required: true },
  },

  data() {
    return {
      isRetrying: false,
      isPickingNewDate: false,
      isLoadingNewDates: false,
      newAvailableDates: [] as TAvailableDeliveryDate[],
      selectedNewDate: null as string | null,
      isLoadingTracking: false,
      trackingResult: null as TDhlTrackingResult | null,
      trackingError: null as string | null,
      isLoadingLabel: false,
      labelError: null as string | null,
      qrCodeUrl: null as string | null,
      isLoadingQrCode: false,
      qrCodeError: null as string | null,
      mountedAt: Date.now(),
      nowTick: Date.now(),
      stuckCheckInterval: null as ReturnType<typeof setInterval> | null,
    }
  },

  computed: {
    ...mapState(useAccountStore, ['labAccount']),
    ...mapState(useAppStore, ['currentLocale']),

    dhlDateLocale(): string {
      return this.currentLocale === 'da' ? 'da-DK' : 'en-GB'
    },

    failedStepError(): string | null {
      const { ratesCheck, shipmentCreation, pickupCancellation } = this.shipment.steps
      if (ratesCheck.status === 'failed') return ratesCheck.error ?? null
      if (shipmentCreation.status === 'failed') return shipmentCreation.error ?? null
      if (pickupCancellation.status === 'failed') return pickupCancellation.error ?? null
      return null
    },

    // `lastAttemptAt` is only set once `runDhlShipment` actually starts — a shipment still
    // 'pending' (never attempted) has no such timestamp, so this component's own mount time
    // is the best available reference for "how long has this been spinning".
    isStuck(): boolean {
      if (this.shipment.status !== 'pending' && this.shipment.status !== 'in-progress') return false
      const since = this.shipment.timestamps.lastAttemptAt ?? this.mountedAt
      return this.nowTick - since > STUCK_THRESHOLD_MS
    },

    formattedPickupTime(): string {
      const raw = this.shipment.result?.estimatedPickupTime
      if (!raw) return ''
      const date = new Date(raw)
      return date.toLocaleString(this.dhlDateLocale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    formattedLabArrival(): string {
      const raw = this.shipment.result?.estimatedDeliveryDateAndTime
      if (!raw) return ''
      const date = new Date(raw)
      return date.toLocaleString(this.dhlDateLocale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      })
    },

    shipmentTypeIsPrivateDropPoint(): boolean {
      return this.shipment.type === 'private-drop-point'
    },

    shipmentTypeLabel(): string {
      return this.shipmentTypeIsPrivateDropPoint
        ? this.$t('area.dhl.order.message.private-drop-point')
        : this.$t('area.dhl.order.message.service-point-dropoff')
    },
  },

  watch: {
    'shipment.result.labelFreeCodePath': {
      immediate: true,
      handler(): void {
        this.loadQrCodeUrl()
      },
    },
  },

  methods: {
    ...mapActions(useOrderStore, ['retryDhlShipment']),

    async loadQrCodeUrl(): Promise<void> {
      const path = this.shipment.result?.labelFreeCodePath
      if (!path) {
        this.qrCodeUrl = null
        return
      }
      this.isLoadingQrCode = true
      this.qrCodeError = null
      try {
        const fileRef = storageRef(storage, path)
        this.qrCodeUrl = await getDownloadURL(fileRef)
      } catch {
        this.qrCodeError = this.$t('area.dhl.shipment.error.qr-fetch-failed')
      } finally {
        this.isLoadingQrCode = false
      }
    },

    async onRetry(): Promise<void> {
      this.isRetrying = true
      try {
        await this.retryDhlShipment(this.orderID)
        useToast().success(this.$t('area.dhl.shipment.message.restarted'))
      } catch {
        useToast().error(this.$t('error.common.standard'))
      } finally {
        this.isRetrying = false
      }
    },

    async onPickNewDate(): Promise<void> {
      this.isPickingNewDate = true
      this.selectedNewDate = null
      const labAccountID = this.labAccount?.id
      if (!labAccountID) return
      this.isLoadingNewDates = true
      try {
        const result = await managementApi(
          'get',
          '/lab-accounts/{labAccountID}/integrations/dhl/drop-point-capability/{dropPointID}/available-dates',
          { path: { labAccountID, dropPointID: this.shipment.dropPointID } }
        )
        this.newAvailableDates = result.success ? result.data.dates : []
      } catch {
        this.newAvailableDates = []
      } finally {
        this.isLoadingNewDates = false
      }
    },

    async onRetryWithNewDate(): Promise<void> {
      if (!this.selectedNewDate) return
      this.isRetrying = true
      try {
        await this.retryDhlShipment(this.orderID, this.selectedNewDate)
        this.isPickingNewDate = false
        useToast().success(this.$t('area.dhl.shipment.message.restarted'))
      } catch {
        useToast().error(this.$t('error.common.standard'))
      } finally {
        this.isRetrying = false
      }
    },

    formatNewDate(dateStr: string): string {
      const [year, month, day] = dateStr.split('-').map(Number) as [number, number, number]
      const date = new Date(year, month - 1, day)
      const formatted = new Intl.DateTimeFormat(this.dhlDateLocale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      }).format(date)
      return formatted.charAt(0).toUpperCase() + formatted.slice(1)
    },

    formatNewCutoff(cutoff: string): string {
      const time = new Intl.DateTimeFormat(this.dhlDateLocale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(new Date(cutoff))
      return this.$t('area.dhl.shipment.message.drop-off-deadline', { time })
    },

    async onDownloadLabel(): Promise<void> {
      const path = this.shipment.result?.labelDocumentPath
      if (!path) return
      this.isLoadingLabel = true
      this.labelError = null
      try {
        const fileRef = storageRef(storage, path)
        const url = await getDownloadURL(fileRef)
        window.open(url, '_blank')
      } catch {
        this.labelError = this.$t('area.dhl.shipment.error.label-fetch-failed')
      } finally {
        this.isLoadingLabel = false
      }
    },

    async onShowTracking(): Promise<void> {
      const labAccountID = this.labAccount?.id
      if (!labAccountID) return
      this.isLoadingTracking = true
      this.trackingError = null
      try {
        const result = await managementApi(
          'get',
          'lab-accounts/{labAccountID}/orders/{orderID}/shipment/tracking',
          { path: { labAccountID, orderID: this.orderID } }
        )
        if (result.success) {
          this.trackingResult = result.data
        } else {
          this.trackingError = this.$t('area.dhl.shipment.error.tracking-fetch-failed')
        }
      } catch {
        this.trackingError = this.$t('area.dhl.shipment.error.tracking-fetch-failed')
      } finally {
        this.isLoadingTracking = false
      }
    },

    formatTrackingEventDate(event: TDhlTrackingEvent): string {
      if (!event.date) return ''
      const date = new Date(`${event.date}T${event.time ?? '00:00:00'}`)
      if (Number.isNaN(date.getTime())) return `${event.date} ${event.time ?? ''}`.trim()
      return date.toLocaleString(this.dhlDateLocale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },

  mounted(): void {
    this.stuckCheckInterval = setInterval(() => {
      this.nowTick = Date.now()
    }, 10_000)
  },

  beforeUnmount(): void {
    if (this.stuckCheckInterval) clearInterval(this.stuckCheckInterval)
  },
}
</script>
